import {
  generateArraySchema,
  generateBooleanSchema,
  generateColorSchema,
  generateContainerSchema,
  generateNumberSchema,
  generateObjectSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import { TextParticleSpec } from "components/cards/particles/YinzCam";

const FG_10_BG = 'color-mix(in srgb, var(--theme-mode-foreground-color) 10%, var(--theme-mode-background-color))';
const FG_25_BG = 'color-mix(in srgb, var(--theme-mode-foreground-color) 25%, var(--theme-mode-background-color))';
const FG_75_BG = 'color-mix(in srgb, var(--theme-mode-foreground-color) 75%, var(--theme-mode-background-color))';

export const specification = new BaseAtomSpec("Ball by Ball", generateRootObjectSchema({
  container: generateAtomContainerSchema({
    defaultValues: {
      themeMode: "primary",
      background: { visible: true, color: 'var(--theme-mode-background-color)' },
      border: { visible: true, width: '1px', style: 'solid', color: FG_25_BG },
    },
  }),

  innings: generateArraySchema(
    "Innings",
    "Each inning tab with its list of overs.",
    generateObjectSchema("Inning", "A single inning.", {
      label: generateStringSchema("Label", "Tab label (team tricode recommended, e.g. \"TRI1 INNINGS\")."),
      overs: generateArraySchema(
        "Overs",
        "Overs in this inning, most-recent-first.",
        generateObjectSchema("Over", "A single over.", {
          id: generateStringSchema("ID", "Stable key for this over."),
          overLabel: generateStringSchema("Over Label", "e.g. \"Over 62\"."),
          summaryText: generateStringSchema(
            "Summary Text",
            "Right-aligned summary on the collapsed row, e.g. \"11 runs | b: DS Thilakartne\"."
          ),
          balls: generateArraySchema(
            "Balls",
            "Up to six deliveries (more if extras push the over past six).",
            generateObjectSchema("Ball", "A single delivery.", {
              outcome: generateStringSchema(
                "Outcome",
                "Raw outcome token (e.g. \"4\", \".\", \"W\", \"6\", \"1\", \"WD\", \"NB\")."
              ),
              timestamp: generateStringSchema("Timestamp", "Over.ball, e.g. \"62.6\"."),
              bowler: generateStringSchema("Bowler", "Bowler name."),
              batter: generateStringSchema("Batter", "Batter name."),
              runsLabel: generateStringSchema(
                "Runs Label",
                "Short headline, e.g. \"4 runs.\", \"Six!\", \"Out!\", \"Wide!\"."
              ),
              commentary: generateStringSchema("Commentary", "Full ball description."),
            })
          ),
        })
      ),
    })
  ),
  selectedInningIndex: generateNumberSchema(
    "Selected Inning Index",
    "Zero-based index of the initially selected inning.",
    { defaultValue: 0 }
  ),
  expandedOverIndex: generateNumberSchema(
    "Expanded Over Index",
    "Zero-based index of the initially expanded over (user clicks override).",
    { nullable: true }
  ),

  // Section containers (background / border / padding / etc.) — defaults dropped; configure per-instance.
  tabsStripContainer: generateContainerSchema("Tabs Strip", "Container styling for the inning tabs strip."),
  tabContainer: generateContainerSchema("Inning Tab", "Container styling for an unselected inning tab."),
  selectedTabContainer: generateContainerSchema("Selected Inning Tab", "Container styling for the selected inning tab."),
  overContainer: generateContainerSchema("Over Section", "Container styling for each over section."),
  ballRowContainer: generateContainerSchema("Ball Row", "Container styling for each expanded ball row."),

  // Atomic style fields
  badgeSize: generateStringSchema("Badge Size", "Width and height of the outcome badges.", {
    defaultValue: '2.5rem',
  }),
  badgeRadius: generateStringSchema("Badge Radius", "Corner radius of the outcome badges.", {
    defaultValue: '0.375rem',
  }),
  badgeSizeCompact: generateStringSchema(
    "Badge Size (Compact)",
    "Badge size at narrow container widths (≤ 26rem).",
    { defaultValue: '2rem' }
  ),
  badgeRadiusCompact: generateStringSchema(
    "Badge Radius (Compact)",
    "Badge radius at narrow container widths (≤ 26rem).",
    { defaultValue: '0.25rem' }
  ),
  chevronSize: generateStringSchema("Chevron Size", "Width and height of the over header chevron icon.", {
    defaultValue: '1.5rem',
  }),
  chevronColor: generateColorSchema("Chevron Color", "Color of the over header chevron icon.", {
    defaultValue: '#FFFFFF',
  }),
  gapBallStrip: generateStringSchema("Ball Strip Gap", "Gap between badges in the collapsed ball strip.", {
    defaultValue: '0.375rem',
  }),
  gapOverMeta: generateStringSchema("Over Meta Gap", "Gap between the ball strip and the over summary text.", {
    defaultValue: '0.5rem',
  }),
  gapBallRow: generateStringSchema("Ball Row Gap", "Gap inside an expanded ball row between the badge and body.", {
    defaultValue: '1rem',
  }),

  // Badge text color (by score outcome) — overrides the badge text color per
  // category; the badge background stays driven by the cricket profile.
  badgeTextColorBoundary: generateColorSchema(
    "Badge Text — Boundary",
    "Text color for boundary badges (4 and 6).",
    { defaultValue: '#FFFFFF' }
  ),
  badgeTextColorWicket: generateColorSchema("Badge Text — Wicket", "Text color for wicket badges (W).", {
    defaultValue: '#FFFFFF',
  }),
  badgeTextColorRuns: generateColorSchema(
    "Badge Text — Dots & Runs",
    "Text color for dot-ball and run badges (., 0, 1, 2, 3, 5).",
    { defaultValue: '#222222' }
  ),
  badgeTextColorExtras: generateColorSchema(
    "Badge Text — Extras",
    "Text color for extras badges (Wd, Nb, Lb, B).",
    { defaultValue: '#FFFFFF' }
  ),

  // Dividers
  showOverDivider: generateBooleanSchema(
    "Show Over Divider",
    "Show the divider line beneath each over section.",
    { defaultValue: true }
  ),
  showCommentaryDivider: generateBooleanSchema(
    "Show Commentary Divider",
    "Show the divider line above the per-ball commentary text.",
    { defaultValue: true }
  ),
  commentaryDividerColor: generateColorSchema(
    "Commentary Divider Color",
    "Color of the divider line above the per-ball commentary text.",
    { defaultValue: FG_10_BG }
  ),

  // Typography
  txtInningTab: TextParticleSpec.generateSchema("Inning Tab Text", "Typography for the inning tab strip.", {
    defaultValues: {
      size: '1rem',
      weight: '700',
      style: 'Bold',
      transform: 'uppercase',
      color: FG_75_BG,
    },
  }),
  selectedTxtInningTab: TextParticleSpec.generateSchema(
    "Selected Inning Tab Text",
    "Typography for the selected inning tab strip.",
    {
      defaultValues: {
        size: '1rem',
        weight: '700',
        style: 'Bold',
        transform: 'uppercase',
        color: 'var(--theme-mode-background-color)',
      },
    }
  ),
  txtOverLabel: TextParticleSpec.generateSchema("Over Label Text", "Typography for the collapsed over header.", {
    defaultValues: { size: '1rem', weight: '500', color: 'var(--theme-mode-foreground-color)' },
  }),
  txtOverSummary: TextParticleSpec.generateSchema("Over Summary Text", "Typography for the over summary line.", {
    defaultValues: { size: '1rem', weight: '400', color: FG_75_BG },
  }),
  txtBadgeSm: TextParticleSpec.generateSchema(
    "Small Badge Text",
    "Typography for the small outcome badges in the strip.",
    { defaultValues: { weight: '700', size: '0.75rem', style: 'bold' } }
  ),
  txtBadgeLg: TextParticleSpec.generateSchema(
    "Large Badge Text",
    "Typography for the badges in expanded ball rows.",
    { defaultValues: { weight: '700', size: '1rem', style: 'bold' } }
  ),
  txtTimestamp: TextParticleSpec.generateSchema(
    "Timestamp Text",
    "Typography for the over.ball timestamp on expanded rows.",
    { defaultValues: { color: 'var(--theme-mode-foreground-color)', weight: '500', size: '1rem' } }
  ),
  txtMatchup: TextParticleSpec.generateSchema(
    "Matchup Text",
    "Typography for the bowler→batter line on expanded rows.",
    { defaultValues: { size: '1.25rem', weight: '500', color: 'var(--theme-mode-foreground-color)' } }
  ),
  txtRunsLabel: TextParticleSpec.generateSchema(
    "Runs Label Text",
    "Typography for the runs label on expanded rows.",
    { defaultValues: { color: '#D5DDF6' } }
  ),
  txtCommentary: TextParticleSpec.generateSchema(
    "Commentary Text",
    "Typography for the per-ball commentary line.",
    {
      defaultValues: {
        size: '1rem',
        weight: '400',
        color: '#D5DDF6',
        lineHeight: '1',
      },
      uneditableFields: { text: true },
    }
  ),
}));
